import React, { useState, useEffect } from 'react';
import { db } from '@/config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { Loader2, CheckCircle2, XCircle, Clock, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ReportProgress {
  status: 'pending' | 'downloading' | 'processing' | 'completed' | 'failed';
  message: string;
  currentBatch?: number;
  totalBatches?: number;
  recordsProcessed?: number;
  totalRecords?: number;
  completedAt?: { seconds: number };
  error?: string;
}

interface ProcessingStatus {
  overallStatus: 'initializing' | 'processing' | 'completing' | 'completed' | 'failed';
  completedReports: number;
  totalReports: number;
  companyName: string;
  profileName: string;
  reports: { [reportType: string]: ReportProgress };
  dailyMetrics: {
    status: 'pending' | 'processing' | 'completed' | 'failed';
    message: string;
  };
  startedAt: { seconds: number };
  lastUpdatedAt: { seconds: number };
}

interface Props {
  companyName: string;
  onComplete?: () => void;
}

const ProcessingStatusDisplay: React.FC<Props> = ({ companyName, onComplete }) => {
  const [status, setStatus] = useState<ProcessingStatus | null>(null);

  // Monotonic UI state to avoid regressions in the progress display when workers update concurrently
  const [displayedCompletedReports, setDisplayedCompletedReports] = useState<number>(0);
  const prevReportProgressRef = React.useRef<Record<string, { recordsProcessed: number; currentBatch: number; totalRecords: number }>>({});

  const [elapsedTime, setElapsedTime] = useState<string>('0s');
  const [nextCheckIn, setNextCheckIn] = useState<number>(0); // seconds until next check

  useEffect(() => {
    // Listen to status updates in real-time
    const statusRef = doc(db, 'amazon_reports', companyName, 'status', 'processing_status');
    
    const unsubscribe = onSnapshot(statusRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as ProcessingStatus;

        // Update monotonic completedReports (never go backwards)
        setDisplayedCompletedReports(prev => {
          const incoming = data.completedReports || 0;
          // if overallStatus is completed, ensure we show full totalReports
          if (data.overallStatus === 'completed') {
            return data.totalReports;
          }
          return Math.max(prev, incoming);
        });

        // Update per-report monotonic values to prevent UI from showing decreases
        for (const [reportType, progress] of Object.entries(data.reports || {})) {
          const prev = prevReportProgressRef.current[reportType] || { recordsProcessed: 0, currentBatch: 0, totalRecords: 0 };
          prevReportProgressRef.current[reportType] = {
            recordsProcessed: Math.max(prev.recordsProcessed || 0, progress.recordsProcessed || 0),
            currentBatch: Math.max(prev.currentBatch || 0, progress.currentBatch || 0),
            totalRecords: Math.max(prev.totalRecords || 0, progress.totalRecords || 0)
          };
        }

        setStatus(data);
        
        // Only trigger completion callback when FULLY done with NO failed reports
        if (data.overallStatus === 'completed' && onComplete) {
          const failedReports = Object.values(data.reports).filter(r => r.status === 'failed');
          if (failedReports.length === 0) {
            setTimeout(onComplete, 2000); // 2 second delay to show completion
          }
        }
      }
    });

    return () => unsubscribe();
  }, [companyName, onComplete]);

  useEffect(() => {
    // Update elapsed time and next check countdown every second
    if (!status) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const startTime = status.startedAt.seconds * 1000; // Use startedAt instead of lastUpdatedAt
      const elapsed = Math.floor((now - startTime) / 1000);
      
      if (elapsed < 60) {
        setElapsedTime(`${elapsed}s`);
      } else if (elapsed < 3600) {
        setElapsedTime(`${Math.floor(elapsed / 60)}m ${elapsed % 60}s`);
      } else {
        setElapsedTime(`${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`);
      }

      // Calculate next check countdown based on status
      // Reports are checked with exponential backoff: 2min → 5min → 10min → 15min
      const pendingReportsCount = Object.values(status.reports).filter(
        r => r.status === 'pending' || r.status === 'downloading'
      ).length;
      
      if (pendingReportsCount > 0) {
        // Estimate next check interval (simplify to 5-minute cycles for user display)
        const minutesSinceStart = Math.floor(elapsed / 60);
        const checkCycle = Math.floor(minutesSinceStart / 5);
        const nextCheckMinute = (checkCycle + 1) * 5;
        const secondsUntilNextCheck = Math.max(0, (nextCheckMinute * 60) - elapsed);
        setNextCheckIn(secondsUntilNextCheck);
      } else {
        setNextCheckIn(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  if (!status) {
    return (
      <Card className="bg-gray-800 border-gray-700 p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
          <span className="text-gray-300">Initializing report processing...</span>
        </div>
      </Card>
    );
  }

  const overallProgress = status ? Math.min(100, (displayedCompletedReports / status.totalReports) * 100) : 0;
  const reportEntries = status ? Object.entries(status.reports).sort((a, b) => a[0].localeCompare(b[0])) : [];

  const pendingReportsCount = Object.values(status.reports).filter(
    r => r.status === 'pending' || r.status === 'downloading'
  ).length;

  return (
    <Card className="bg-gray-800 border-gray-700 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-semibold text-white">
            Processing {status.companyName}
          </h3>
          <span className="text-sm text-gray-400">{elapsedTime} elapsed</span>
        </div>
        
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300">
              Overall Progress: {status.completedReports}/{status.totalReports} reports
            </span>
            <span className="text-gray-400">{Math.round(overallProgress)}%</span>
          </div>
          <Progress value={overallProgress} className="h-2 bg-gray-700" />
        </div>
      </div>

      {/* Time Expectation Alert */}
      {pendingReportsCount > 0 && (
        <Alert className="mb-4 bg-blue-900/20 border-blue-700">
          <Info className="h-4 w-4 text-blue-400" />
          <AlertDescription className="text-sm text-blue-200">
            <div className="space-y-1">
              <p className="font-medium">Report processing can take 30 minutes to 2 hours (or longer).</p>
              <p className="text-xs text-blue-300">
                Amazon generates reports asynchronously. We check for updates every 5-15 minutes.
                {nextCheckIn > 0 && (
                  <span className="block mt-1 font-medium">
                    Next check in: {Math.floor(nextCheckIn / 60)}m {nextCheckIn % 60}s
                  </span>
                )}
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Report Status Grid - 2 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {reportEntries.map(([reportType, progress]) => {
          const displayed = prevReportProgressRef.current[reportType] || {
            recordsProcessed: progress.recordsProcessed || 0,
            totalRecords: progress.totalRecords || 0,
            currentBatch: progress.currentBatch || 0
          };
          return (
            <ReportStatusLine
              key={reportType}
              reportType={reportType}
              progress={progress}
              displayedProgress={displayed}
            />
          );
        })}
      </div>

      {/* Daily Metrics Status */}
      <div className="pt-4 border-t border-gray-700">
        <DailyMetricsStatus
          status={status.dailyMetrics.status}
          message={status.dailyMetrics.message}
        />
      </div>

      {/* Completion Message */}
      {status.overallStatus === 'completed' && (
        <div className="mt-4 p-3 bg-green-900/20 border border-green-700 rounded-lg">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">All reports processed successfully!</span>
          </div>
        </div>
      )}
    </Card>
  );
};

interface ReportStatusLineProps {
  reportType: string;
  progress: ReportProgress;
  displayedProgress: { recordsProcessed: number; totalRecords: number; currentBatch?: number };
}
 
const ReportStatusLine: React.FC<ReportStatusLineProps> = ({ reportType, progress, displayedProgress }) => {
  const getStatusIcon = () => {
    switch (progress.status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'processing':
      case 'downloading':
        return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };
 
  const getStatusColor = () => {
    switch (progress.status) {
      case 'completed':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'processing':
      case 'downloading':
        return 'text-blue-400';
      default:
        return 'text-gray-500';
    }
  };
 
  const formatReportName = (type: string) => {
    // Format with proper capitalization for SP and SB prefixes
    const formatted = type
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
    
    // Ensure SP and SB are capitalized
    return formatted
      .replace(/^Sp\s/, 'SP ')
      .replace(/^Sb\s/, 'SB ');
  };
 
  const recordsProcessedDisp = displayedProgress?.recordsProcessed ?? (progress.recordsProcessed ?? 0);
  const totalRecordsDisp = displayedProgress?.totalRecords ?? (progress.totalRecords ?? 0);
  const recordProgress = totalRecordsDisp ? Math.round((recordsProcessedDisp / totalRecordsDisp) * 100) : 0;
 
  return (
    <div className="flex items-center gap-3 p-2 bg-gray-900/50 rounded">
      {getStatusIcon()}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-white truncate">
            {formatReportName(reportType)}
          </span>
          {progress.status === 'processing' && totalRecordsDisp > 0 && (
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {recordsProcessedDisp.toLocaleString()}/{totalRecordsDisp.toLocaleString()}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2 mt-1">
          <p className={`text-xs ${getStatusColor()} truncate`}>
            {progress.message}
          </p>
        </div>
 
        {/* Progress bar for processing reports */}
        {progress.status === 'processing' && totalRecordsDisp > 0 && (
          <Progress
            value={recordProgress}
            className="h-1 mt-1 bg-gray-800"
          />
        )}
      </div>
    </div>
  );
};

interface DailyMetricsStatusProps {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message: string;
}

const DailyMetricsStatus: React.FC<DailyMetricsStatusProps> = ({ status, message }) => {
  const getIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getColor = () => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'processing':
        return 'text-blue-400';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="flex items-center gap-3">
      {getIcon()}
      <div>
        <span className="text-sm font-medium text-white">Daily Metrics</span>
        <p className={`text-xs ${getColor()} mt-0.5`}>{message}</p>
      </div>
    </div>
  );
};

export default ProcessingStatusDisplay;